// Derivations behind the dashboard stat panels.
//
// These used to be constants in the dashboard store (`getOpenCriticalFaults: ()
// => 6`), so every account saw the same invented numbers. Everything here is
// computed from records the API actually returns; where a field does not exist
// in the data model, the metric returns null and the panel renders a dash
// rather than a plausible-looking lie.

import moment from "moment";
import { failRate } from "@/modules/inspections/inspectionScoring";

// ---------------------------------------------------------------- issues ----

// An issue is open until it is closed; there is no separate "pending" state in
// the model, so the second figure is the overdue subset of the open ones. The
// server counts (GET /issue/metrics); this only derives the percentage.
export function issueMetrics(totals) {
  let open = totals.open;
  return {
    open: open,
    closed: totals.closed,
    overdue: totals.overdue,
    // Share of the open backlog that has blown its due date. Null when nothing
    // is open, so the panel shows "—" instead of 0%.
    overduePercent: open ? (totals.overdue / open) * 100 : null,
    vehiclesAffected: totals.vehicles_affected
  };
}

// -------------------------------------------------------------- renewals ----

// Mirrors what the server does for vehicle renewals (dashboard manager:
// start_date <= now is overdue, later is due soon) so a contact renewal is
// counted the same way a vehicle renewal is.
export function renewalMetrics(renewals) {
  let now = moment();
  let overdue = 0;
  let dueSoon = 0;

  (renewals || []).forEach(renewal => {
    if (!renewal.start_date) return;
    if (moment(renewal.start_date).isAfter(now)) {
      dueSoon += 1;
    } else {
      overdue += 1;
    }
  });

  return { overdue: overdue, due_soon: dueSoon };
}

// ---------------------------------------------------------- inspections ----

// The server scores records (GET /inspectionRecord/metrics) inside windows
// taken from here, so weeks and days keep the browser's locale and time zone.
export function inspectionMetricWindows(days) {
  let startOfWeek = moment().startOf("week");
  return {
    week_start: startOfWeek.toISOString(),
    week_end: moment().endOf("day").toISOString(),
    last_week_start: startOfWeek.clone().subtract(1, "week").toISOString(),
    daily_from: moment().subtract(days - 1, "days").startOf("day").toISOString(),
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone
  };
}

// Item-level failure rate for the current week against the one before it. Rates
// are null when no check was answered in that week — a week with no inspections
// is not a week with a 0% failure rate.
export function inspectionFailureRates(metrics) {
  return {
    currentWeek: failRate(metrics.this_week),
    lastWeek: failRate(metrics.last_week),
    currentWeekAnswered: metrics.this_week.answered,
    lastWeekAnswered: metrics.last_week.answered
  };
}

// Submissions that carried at least one failed check, over every record.
// `failing` and `clean` come from the same count so they always sum to the
// number of submissions that scored anything.
export function inspectionOutcomeMetrics(metrics) {
  let totals = metrics.all;
  return {
    submissions: totals.submissions,
    failing: totals.failing,
    clean: totals.clean,
    failingPercent: totals.submissions ? (totals.failing / totals.submissions) * 100 : null
  };
}

// Daily submission counts for the last `days` days, oldest first, with empty
// days filled in as zero so the line does not skip weekends.
export function inspectionDailySubmissions(daily, days) {
  let counts = {};
  (daily || []).forEach(row => { counts[row.day] = row; });

  let buckets = [];
  for (let offset = days - 1; offset >= 0; offset--) {
    let day = moment().subtract(offset, "days").startOf("day");
    let row = counts[day.format("YYYY-MM-DD")];
    buckets.push({ label: day.format("ddd"), date: day, total: row ? row.total : 0, failing: row ? row.failing : 0 });
  }
  return buckets;
}

// ------------------------------------------------------------- services ----

// Monthly service spend, oldest first. Falls back to the start date when a
// service has not been completed, which is how the service list itself dates a
// record still in progress.
export function serviceCostByMonth(services, months) {
  let buckets = [];
  let index = {};

  for (let offset = months - 1; offset >= 0; offset--) {
    let month = moment().subtract(offset, "months").startOf("month");
    let bucket = { label: month.format("MMM"), month: month, total: 0 };
    index[month.format("YYYY-MM")] = bucket;
    buckets.push(bucket);
  }

  (services || []).forEach(service => {
    let when = service.completion_date_time || service.start_date_time;
    if (!when) return;
    let bucket = index[moment(when).format("YYYY-MM")];
    if (!bucket) return;
    bucket.total += service.grand_total || 0;
  });

  return buckets;
}
